import { Form, Formik } from "formik";
import { useState } from "react";
import { Alert } from "@/core/components/ui/alert/alert";
import { AutoComplete } from "@/core/components/ui/autocomplete/autocomplete";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input/input";
import { Stack } from "@/core/components/ui/stack";
import type { IEnrichedBusiness } from "@/business/business";
import { extractErrorMessage } from "@/core/api/errors";
import type { OnboardingStepPane } from "@/onboarding/routes";
import {
  buildCompanyPayload,
  BUSINESS_TIME_ZONE_OPTIONS,
  getInitialBusinessSetupValues,
  validateCompanyStep,
} from "./business-setup";
import { FieldError } from "./field-error";
import { useSaveSimpleBusinessSetup } from "./query";
import { StepLayout } from "./step-layout";

interface CompanyStepScreenProps {
  business: Pick<
    IEnrichedBusiness,
    | "id"
    | "name"
    | "time_zone_label"
    | "service_titan_tenant_id"
    | "current_pay_period_template"
  >;
  activePane: OnboardingStepPane;
  onContinue: () => void;
  onBack: () => void;
}

export const CompanyStepScreen = ({
  business,
  activePane,
  onContinue,
  onBack,
}: CompanyStepScreenProps) => {
  const initialValues = getInitialBusinessSetupValues(business);
  const [timeZoneSearch, setTimeZoneSearch] = useState(initialValues.timeZone);
  const saveMutation = useSaveSimpleBusinessSetup(business.id);

  return (
    <StepLayout
      activePane={activePane}
      title="Tell us about your company"
      subtitle="We use the time zone to line your pay periods up correctly."
    >
      <Formik
        initialValues={initialValues}
        validate={validateCompanyStep}
        onSubmit={(values, helpers) => {
          helpers.setStatus(undefined);
          saveMutation.mutate(buildCompanyPayload(values), {
            onSuccess: () => {
              helpers.setSubmitting(false);
              onContinue();
            },
            onError: (error) => {
              helpers.setStatus(extractErrorMessage(error).message);
              helpers.setSubmitting(false);
            },
          });
        }}
      >
        {({
          values,
          errors,
          status,
          submitCount,
          isSubmitting,
          setFieldValue,
        }) => (
          // noValidate: this form shows its own message, so the browser must not
          // pre-empt it with a native bubble on a required field.
          <Form noValidate>
            <Stack gap="lg" align="stretch" fullWidth>
              <Card className="w-full border-stroke-tertiary shadow-sm">
                <CardContent className="p-6 sm:p-8">
                  <Stack gap="lg" align="stretch" fullWidth>
                    {/* Input styles the field for an error but never writes the words,
                        so every field here pairs the state with its message. */}
                    <Stack gap="xs" fullWidth>
                      <Input
                        name="name"
                        label="Company name"
                        aria-label="Company name"
                        value={values.name}
                        onChange={(event) =>
                          setFieldValue("name", event.target.value)
                        }
                        error={submitCount > 0 ? errors.name : undefined}
                        fullWidth
                        required
                      />
                      <FieldError
                        message={submitCount > 0 ? errors.name : undefined}
                      />
                    </Stack>

                    <Stack gap="xs" fullWidth>
                      <AutoComplete
                        label="Time zone"
                        placeholder="Select a time zone"
                        selectedValue={values.timeZone}
                        onSelectedValueChange={(value) =>
                          setFieldValue("timeZone", value)
                        }
                        searchValue={timeZoneSearch}
                        onSearchValueChange={setTimeZoneSearch}
                        error={submitCount > 0 ? errors.timeZone : undefined}
                        items={BUSINESS_TIME_ZONE_OPTIONS}
                        required
                        fullWidth
                      />
                      <FieldError
                        message={submitCount > 0 ? errors.timeZone : undefined}
                      />
                    </Stack>

                    <Input
                      name="serviceTitanTenantId"
                      label="ServiceTitan tenant ID"
                      aria-label="ServiceTitan tenant ID"
                      value={values.serviceTitanTenantId}
                      onChange={(event) =>
                        setFieldValue(
                          "serviceTitanTenantId",
                          event.target.value,
                        )
                      }
                      fullWidth
                    />

                    {/* The banner is only for a save that failed: every validation
                          message sits on its own field. */}
                    {status && <Alert variant="destructive">{status}</Alert>}
                  </Stack>
                </CardContent>
              </Card>

              {/* Grouped bottom-right: two buttons pushed to opposite edges read as two
                  stranded controls, a pair reads as one decision. */}
              <Stack
                direction="row"
                gap="sm"
                justify="end"
                align="center"
                fullWidth
              >
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  disabled={isSubmitting}
                  onClick={onBack}
                >
                  Back
                </Button>
                <Button type="submit" size="sm" loading={isSubmitting}>
                  Continue
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </StepLayout>
  );
};
